import { useEffect, useState } from 'react';
import { AiBalanceBody } from '@components/AiBalanceBody';
import { aiErrorMessage } from '@services/ai-errors';
import { aiExplainMyBalance, aiMemberStatus, aiMyBalanceExplanation } from '@services/ai.service';
import { t } from '@services/i18n';
import type { AiBalanceExplanation } from '@shared/types/ai';

/**
 * F2, member side: "why is my balance this?" on the activity page.
 *
 * Three rules make this safe to put in front of visitors.
 *
 * It renders nothing at all unless the site has switched the member-facing
 * explainer on, so a site that never enabled it has no new surface.
 *
 * It never generates on load. The card shows the saved answer if there is one,
 * and asking for a new one is a click, because every generation is billed to
 * the site and the member gateway has no rate limiting of its own. The server
 * applies a cooldown on top of that, so a member who keeps clicking gets the
 * same answer back rather than a bill.
 *
 * Everything it shows carries the AI disclosure, from {@see AiBalanceBody}.
 */
export function AiMyBalance({ currency = '' }: { currency?: string }) {
  const [allowed, setAllowed] = useState(false);
  const [data, setData] = useState<AiBalanceExplanation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;

    aiMemberStatus()
      .then((status) => {
        if (!live || !status.explainer) return;
        setAllowed(true);

        return aiMyBalanceExplanation(currency).then((res) => {
          if (live) setData(res.explanation);
        });
      })
      .catch(() => {
        // Switched off, not connected, or unreachable: show nothing at all
        // rather than a control a member cannot use.
      });

    return () => {
      live = false;
    };
  }, [currency]);

  if (!allowed) return null;

  const run = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      setData(await aiExplainMyBalance(currency));
    } catch (e) {
      setError(aiErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rw-card">
      <div className="rw-card-h">
        <div>
          <h3 className="rw-card-t">{t('COM_REWARDIFY_UAI_TITLE', 'Why is my balance this?')}</h3>
          <p className="rw-card-s">
            {t('COM_REWARDIFY_UAI_SUB', 'A short summary of your own history: what you earned, and what expires when.')}
          </p>
        </div>
        <button type="button" className="rw-btn rw-btn-sm" onClick={() => void run()} disabled={busy}>
          {busy
            ? t('COM_REWARDIFY_UAI_WORKING', 'Reading your history…')
            : data
              ? t('COM_REWARDIFY_UAI_AGAIN', 'Ask again')
              : t('COM_REWARDIFY_UAI_RUN', 'Explain my balance')}
        </button>
      </div>

      {error && (
        <p style={{ fontSize: 12.5, color: 'var(--rw-neg, #dc2626)', margin: 0 }}>{error}</p>
      )}

      {!data && !error && (
        <p className="rw-fg2" style={{ fontSize: 12.5, margin: 0 }}>
          {t('COM_REWARDIFY_UAI_EMPTY', 'Ask and you will get a plain summary of where your points came from.')}
        </p>
      )}

      {data && <AiBalanceBody data={data} />}
    </section>
  );
}
